import { advisorAdsCopy, moonPresets, type AdvisorAdType } from "../config/sections"
import type { Advisor } from "../types"

import Dot from "@/assets/svg/advisors/Dot.svg?react"
import MessageTick from "@/assets/svg/lpr4/advisor/message-tick.svg?react"
import Chevron from "@/assets/svg/lpr4/advisor/chevron-right.svg?react"
import MoonEclipse from "@/assets/svg/lpr4/advisor/moon-eclipse.svg?react"
import MoonStar from "@/assets/svg/lpr4/advisor/moon-star.svg?react"
import MoonHalf from "@/assets/svg/lpr4/advisor/moon-half.svg?react"
import flowerBg from "@/assets/img/lpr4/advisor/promo/promo-bg-flowers.avif"
import planetBg from "@/assets/img/lpr4/advisor/promo/promo-bg-planet.avif"

const presetIcons = [<MoonEclipse />, <MoonStar />, <MoonHalf />]

interface AdvisorAdsProps {
  advisor: Advisor
  type: AdvisorAdType
  onChatClick: (advisor: Advisor) => void
  onPresetClick: (preset: string, advisor: Advisor) => void
}

export function AdvisorAds({ advisor, type, onChatClick, onPresetClick }: AdvisorAdsProps) {
  const copy = advisorAdsCopy[type]

  return (
    <section className="relative w-full overflow-hidden bg-dark-base px-4 pt-[70px]">
      <div className="relative z-[2] max-w-[180px]">
        <h2 className="font-display text-2xl font-medium text-purple-light">{copy.title}</h2>
        <p className="pt-3 font-body text-base text-purple-light/70">{copy.subtitle}</p>
      </div>

      <div className="relative z-[2] mt-6 select-none rounded-3xl bg-purple-light/10 p-4 backdrop-blur-[30px]">
        <div className="flex items-center">
          <div className="relative inline-flex size-14 rounded-full bg-gradient-to-br from-peach via-peach to-purple-accent p-1">
            <span className="absolute inset-px rounded-full bg-dark-base" />
            <img
              src={advisor.photo}
              alt={advisor.name}
              className="relative size-full rounded-full object-cover object-top"
            />
          </div>
          <div className="ml-2 flex flex-col">
            <p className="font-display text-sm font-medium text-white">{advisor.name}</p>
            <p className="font-body text-xs text-purple-light/70">{advisor.exp}</p>
          </div>
          <div className="ml-auto flex h-7 items-center justify-center gap-1 text-nowrap rounded-2xl bg-green/20 pl-2.5 pr-3 font-body text-xs text-green [&_svg]:size-1.5">
            <Dot /> Online
          </div>
        </div>

        <div className="relative mt-3 max-w-[296px] rounded-2xl bg-[#362d45] px-4 py-3 font-body text-base text-purple-light [&_svg]:absolute [&_svg]:-bottom-0.5 [&_svg]:-left-0.5 [&_svg]:min-h-3 [&_svg]:min-w-3.5 [&_svg]:text-[#362d45]">
          {copy.message}
          <MessageTick />
        </div>

        {type === "dream" ? (
          <button
            type="button"
            onClick={() => onChatClick(advisor)}
            className="mt-4 block h-12 rounded-[80px] bg-gradient-to-b from-peach to-purple-accent px-[54px] font-body text-base font-semibold text-white"
          >
            Start chat
          </button>
        ) : (
          <div className="ml-auto mt-4 flex w-[90%] flex-col gap-2">
            {moonPresets.map((preset, index) => (
              <button
                type="button"
                key={preset}
                onClick={() => onPresetClick(preset, advisor)}
                className="relative flex select-none items-center gap-2 rounded-xl border border-purple-light/10 px-[18px] py-3.5 text-left font-body text-base text-purple-light [&_svg]:size-6"
              >
                <span className="size-6">{presetIcons[index]}</span>
                {preset}
                <span className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center justify-center text-purple-light">
                  <Chevron />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <img
        src={type === "dream" ? flowerBg : planetBg}
        alt=""
        className="absolute -top-[50px] right-0 z-[1] w-full"
      />
    </section>
  )
}
